/**
 * Rejeu de la file d'écritures hors ligne (§ 11.3).
 *
 * Partagé entre l'application (src/lib/api.js) et le service worker
 * (src/sw.js) : le Background Sync se déclenche aussi lorsque aucun onglet
 * n'est ouvert, et doit alors rejouer la file lui-même.
 */
import { idb } from './idb.js';

/** Au-delà, une écriture rejouée sans succès est abandonnée et signalée. */
export const MAX_ESSAIS = 5;

/**
 * Un refus définitif du serveur (4xx) retire l'écriture de la file ; une panne
 * passagère (5xx, 408, 429) l'y laisse. Retirer sur panne passagère
 * détruirait la saisie de l'utilisateur, qui n'a aucun moyen de la retrouver.
 */
export function estDefinitif(r) {
  return typeof r.status === 'number' && r.status >= 400 && r.status < 500
    && r.status !== 408 && r.status !== 429;
}

/**
 * Rejoue chaque élément de la file via `envoyer`.
 * envoyer(item) → { ok, status?, error?, offline? }
 * Retourne { done, failed:[{label, error}] } — aucune perte silencieuse.
 */
export async function rejouerFile(envoyer) {
  const items = await idb.queueAll();
  let done = 0;
  const failed = [];
  for (const item of items) {
    const r = await envoyer(item);
    if (r.ok && !r.queued) {
      await idb.queueDelete(item.id);
      done++;
      continue;
    }
    if (r.offline) continue; // réseau reperdu : l'élément reste en file.
    const essais = (item.essais || 0) + 1;
    if (estDefinitif(r) || essais >= MAX_ESSAIS) {
      failed.push({ label: item.label || item.path, error: r.error || 'Échec' });
      await idb.queueDelete(item.id);
    } else {
      await idb.queuePatch(item.id, { essais, derniereErreur: r.error });
    }
  }
  return { done, failed };
}
